import {
  ApprovalStatus,
  convertDocumentStatus,
  DocumentStatus,
} from '../../../../../api/enumerations';
import {
  ChangeRequestStatus,
  ChangeRequestType,
  ContractStatus,
  NoticeCompanyUserStatus,
  TermState,
} from '../../../../../api/theHive/notices/enumerations';
import {
  AdditionTermData,
  ChangeRequestCriteriaGroup,
  ChangeRequestData,
  CriteriaAnalysisData,
  DocumentRow,
  MissingRequirementDoc,
  NoticeCompanyTeamCounters,
  NoticeCompanyTeamData,
  NoticeCompanyTeamGroup,
  NoticeCompanyTeamUser,
  RemovalTermData,
  SignedContractData,
} from '../../../../../api/theHive/notices/types';
import { Constants } from '../../../../../constants/hiveNotices';
import {
  IconBlockMS,
  IconCancelMS,
  IconCheckCircleMS,
  IconEditDocumentMS,
  IconFactCheckMS,
  IconScheduleMS,
} from '../../../../../constants/icons';

interface DocumentStatusStyle {
  color: string;
  background: string;
  icon: JSX.Element;
}

const documentStatusStyles: Record<DocumentStatus, DocumentStatusStyle> = {
  [DocumentStatus.PENDING]: {
    color: '#5C5F66',
    background: '#F1F1F3',
    icon: IconScheduleMS,
  },
  [DocumentStatus.UNDER_ANALYSIS]: {
    color: '#1D5FD1',
    background: '#E8F1FE',
    icon: IconFactCheckMS,
  },
  [DocumentStatus.APPROVED]: {
    color: '#1E8A4C',
    background: '#E7F7EE',
    icon: IconCheckCircleMS,
  },
  [DocumentStatus.APPROVED_WITH_RESERVATIONS]: {
    color: '#0B7A5A',
    background: '#E0F2EC',
    icon: IconCheckCircleMS,
  },
  [DocumentStatus.REJECTED]: {
    color: '#FF4C4C',
    background: '#FFEAEA',
    icon: IconCancelMS,
  },
  [DocumentStatus.CANCELED]: {
    color: '#44464C',
    background: '#EDEEF0',
    icon: IconBlockMS,
  },
  [DocumentStatus.REPLACEMENT_REQUEST]: {
    color: '#B25E09',
    background: '#FFF4E5',
    icon: IconEditDocumentMS,
  },
};

export const getDocumentStatusStyle = (
  status: DocumentStatus
): DocumentStatusStyle =>
  documentStatusStyles[status] ?? documentStatusStyles[DocumentStatus.PENDING];

const notSentStatusStyle: DocumentStatusStyle = {
  color: '#5C5F66',
  background: '#F1F1F3',
  icon: IconScheduleMS,
};

export const isMissingRequirement = (
  doc: DocumentRow
): doc is MissingRequirementDoc => doc.status === 'not_sent';

export const getDocumentRowStatusStyle = (
  doc: DocumentRow
): DocumentStatusStyle =>
  isMissingRequirement(doc)
    ? notSentStatusStyle
    : getDocumentStatusStyle(doc.status);

export const getDocumentRowStatusLabel = (doc: DocumentRow): string =>
  isMissingRequirement(doc)
    ? Constants.documentNotSentStatus
    : convertDocumentStatus(doc.status);

export const getDocumentRowName = (doc: DocumentRow): string =>
  ('name' in doc && doc.name) ||
  ('description' in doc && doc.description) ||
  ('display_name' in doc && doc.display_name) ||
  '';

export const getDocumentRowKey = (doc: DocumentRow): string =>
  isMissingRequirement(doc)
    ? `requirement-${doc.document_type}-${doc.name}`
    : `document-${doc.id}-${'name' in doc ? doc.name : ''}`;

// Contrato submetido e ainda sem veredito — o único estado em que o banco pode
// aprovar/reprovar. Equivale a `approval_status == formalization_submitted` no
// backend, que é o que `approve_formalization`/`reject_formalization` exigem: nos
// outros estados a API responde 409. Testar por `uploaded_at` não bastava,
// porque `awaiting_submission` (arquivo enviado, formalização não submetida)
// também tem data de upload.
export const isContractPendingReview = (
  contract: SignedContractData | null | undefined
): boolean => contract?.status === ContractStatus.IN_ANALYSIS;

// Preview em texto puro do motivo de rejeição (que agora é rich text/HTML):
// remove as tags para a prévia na célula da tabela. O texto completo formatado
// é exibido no diálogo (RichTextViewer).
export const stripHtml = (html: string | null | undefined): string =>
  (html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export interface PoleUfSummary {
  uf: string;
  count: number;
}

// A UF can hold several poles, so the chip carries the count instead of
// repeating the same two-letter code.
export const groupRequestedPolesByUf = (
  requests: ChangeRequestData[]
): PoleUfSummary[] => {
  const countByUf = new Map<string, number>();

  requests.forEach((request) => {
    const { notice_coverage: coverage } = request;
    if (!coverage) return;
    countByUf.set(coverage.uf, (countByUf.get(coverage.uf) ?? 0) + 1);
  });

  return Array.from(countByUf.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([uf, count]) => ({ uf, count }));
};

// Several requests can point at the same criteria group (one per criteria), so
// the chips are deduplicated by group id.
export const getRequestedCriteriaGroups = (
  requests: ChangeRequestData[]
): ChangeRequestCriteriaGroup[] => {
  const byId = new Map<number, ChangeRequestCriteriaGroup>();

  requests.forEach(({ criteria_group: group }) => {
    if (group && !byId.has(group.id)) byId.set(group.id, group);
  });

  return Array.from(byId.values());
};

interface TeamMemberStatusStyle {
  label: string;
  color: string;
  background: string;
}

// Only the three accreditation states the analyst acts on are listed here. The
// labels differ from convertNoticeCompanyUserStatus on purpose: that converter
// speaks in distribution terms ("disponível"), this section in accreditation
// terms.
const teamMemberStatusStyles: Partial<
  Record<NoticeCompanyUserStatus, TeamMemberStatusStyle>
> = {
  [NoticeCompanyUserStatus.APPROVED]: {
    label: Constants.teamStatusApproved,
    color: '#1E8A4C',
    background: '#E7F7EE',
  },
  [NoticeCompanyUserStatus.PENDING]: {
    label: Constants.teamStatusInAnalysis,
    color: '#1D5FD1',
    background: '#E8F1FE',
  },
  [NoticeCompanyUserStatus.PAUSED]: {
    label: Constants.teamStatusPaused,
    color: '#B25E09',
    background: '#FFF4E5',
  },
  [NoticeCompanyUserStatus.LEAVING]: {
    label: Constants.teamStatusLeaving,
    color: '#B25E09',
    background: '#FFF4E5',
  },
};

export const getTeamMemberStatusStyle = (
  status: NoticeCompanyUserStatus
): TeamMemberStatusStyle | undefined => teamMemberStatusStyles[status];

export const hasPendingMemberRequest = (user: NoticeCompanyTeamUser): boolean =>
  user.has_pending_change_requests === true;

export const getTeamMemberDisplayStatus = (
  user: NoticeCompanyTeamUser
): NoticeCompanyUserStatus => {
  if (user.status === NoticeCompanyUserStatus.LEAVING) return user.status;

  return hasPendingMemberRequest(user)
    ? NoticeCompanyUserStatus.PENDING
    : user.status;
};

export const isTeamMemberUnderAnalysis = (
  user: NoticeCompanyTeamUser
): boolean =>
  getTeamMemberDisplayStatus(user) === NoticeCompanyUserStatus.PENDING;

// The `status` filter sent to the team endpoint, so draft, rejected, cancelled
// and discredited members are never fetched and the list matches the counters
// the endpoint returns. `paused` is not sent: a paused member is an approved one
// on leave, and the endpoint already returns them under `approved` (the row
// itself still comes with `status: paused`, which is what the badge reads).
export const teamStatusFilter: NoticeCompanyUserStatus[] = [
  NoticeCompanyUserStatus.APPROVED,
  NoticeCompanyUserStatus.PENDING,
];

const pluralize = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

// The endpoint counts by accreditation status, but the badges answer to the
// display status: an approved or paused member with an open request shows as
// "em análise". Moving them here keeps the summary and the rows telling the
// same story. Nothing is invented — each member only leaves the bucket their
// own status put them in.
const shiftCountersByOpenRequests = (
  counters: NoticeCompanyTeamCounters,
  items: NoticeCompanyTeamUser[]
): NoticeCompanyTeamCounters => {
  const moved = items.filter(
    (user) =>
      user.status !== NoticeCompanyUserStatus.PENDING &&
      isTeamMemberUnderAnalysis(user)
  );

  if (moved.length === 0) return counters;

  const movedFrom = (status: NoticeCompanyUserStatus): number =>
    moved.filter((user) => user.status === status).length;

  return {
    ...counters,
    available: Math.max(
      0,
      counters.available - movedFrom(NoticeCompanyUserStatus.APPROVED)
    ),
    paused: Math.max(
      0,
      counters.paused - movedFrom(NoticeCompanyUserStatus.PAUSED)
    ),
    in_analysis: counters.in_analysis + moved.length,
  };
};

// Quick read of the team situation, from the endpoint counters adjusted to the
// open requests. `not_approved` is left out because those members are not
// listed, and zeroed states are dropped.
export const getTeamCountersSummary = (
  team: NoticeCompanyTeamData | undefined
): string => {
  if (!team?.counters) return '';

  const counters = shiftCountersByOpenRequests(team.counters, team.items);

  return [
    counters.available > 0 &&
      pluralize(
        counters.available,
        Constants.teamCounterApprovedSingular,
        Constants.teamCounterApprovedPlural
      ),
    counters.in_analysis > 0 &&
      `${counters.in_analysis} ${Constants.teamCounterInAnalysis}`,
    counters.paused > 0 &&
      pluralize(
        counters.paused,
        Constants.teamCounterPausedSingular,
        Constants.teamCounterPausedPlural
      ),
    (counters.leaving ?? 0) > 0 &&
      `${counters.leaving} ${Constants.teamCounterLeaving}`,
  ]
    .filter((item): item is string => Boolean(item))
    .join(' · ');
};

export interface TeamMemberTagSummary {
  text: string;
  tooltip: string;
}

const maxVisibleTeamTags = 2;

const joinNames = (names: string[]): string =>
  names.length > 1
    ? `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`
    : names[0] ?? '';

// A professional can serve many criteria groups and many poles. Naming them all
// would blow up the accordion header, so only the first two are spelled out and
// the rest collapse into a "+N" suffix — the tooltip carries the full list.
export const summarizeTeamMemberTags = (
  items: NoticeCompanyTeamGroup[],
  singularLabel: string,
  pluralLabel: string
): TeamMemberTagSummary | undefined => {
  const names = items.map((item) => item.name).filter(Boolean);
  if (names.length === 0) return undefined;

  const label = names.length === 1 ? singularLabel : pluralLabel;
  const visible = names.slice(0, maxVisibleTeamTags);
  const hidden = names.length - visible.length;

  return {
    text: `${label} ${joinNames(visible)}${hidden > 0 ? ` +${hidden}` : ''}`,
    tooltip: `${label}: ${names.join(', ')}`,
  };
};

// Every group the COMPANY serves, from the engineering standpoint: the union
// of what the responsible and each collaborator chose, without repeats. It
// comes from the team (and not from `criteria-documents`) because only the
// professional's item carries the owner of the choice — the grouped endpoint
// ignores whose group it is.
export const getTeamCriteriaGroups = (
  team: NoticeCompanyTeamData | undefined
): NoticeCompanyTeamGroup[] => {
  const byId = new Map<number, NoticeCompanyTeamGroup>();

  team?.items.forEach((user) =>
    user.criteria_groups.forEach((group) => {
      if (!byId.has(group.id)) byId.set(group.id, group);
    })
  );

  return Array.from(byId.values()).sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '')
  );
};

// Only ADDITION requests arrive pending (removal, queue withdrawal and offer
// acceptance are born `applied`), and both are engineering matters: a pole
// asks for the CREA/CAU endorsement, a group asks for the criteria
// documents.
const pendingEngineeringRequestTypes = [
  ChangeRequestType.ADD_COVERAGE,
  ChangeRequestType.ADD_CRITERIA,
];

// An already approved company with an open engineering queue: the screen
// opens straight on that tab, otherwise the analyst lands on the legal one
// (already settled) and has to hunt for what came in for them to review. It
// holds for the professional's individual request and for the company's
// self-managed pole/group changes.
export const hasPendingEngineeringAnalysis = (
  approvalStatus: ApprovalStatus | undefined,
  team: NoticeCompanyTeamData | undefined,
  changeRequests: ChangeRequestData[]
): boolean => {
  if (approvalStatus !== ApprovalStatus.APPROVED) return false;

  // Both the professional whose accreditation is under analysis and the
  // approved (or paused) one carrying an open pole/group request: either way
  // there is engineering documentation waiting on this screen.
  const hasPendingTeamMember = Boolean(
    team?.items.some(isTeamMemberUnderAnalysis)
  );

  const hasPendingChangeRequest = changeRequests.some(
    (request) =>
      request.status === ChangeRequestStatus.PENDING &&
      pendingEngineeringRequestTypes.includes(request.request_type)
  );

  return hasPendingTeamMember || hasPendingChangeRequest;
};

// The first member under analysis: it is what the analyst opened the screen
// to see, so its accordion comes expanded. `undefined` when there is none —
// then nothing opens on its own.
export const getFirstPendingTeamMemberId = (
  team: NoticeCompanyTeamData | undefined
): number | undefined => team?.items.find(isTeamMemberUnderAnalysis)?.id;

export const getMemberScopeId = (
  user: NoticeCompanyTeamUser
): number | undefined => (user.is_responsible_seed ? undefined : user.id);

export const getMemberChangeRequestOwnerIds = (
  user: NoticeCompanyTeamUser
): (number | null)[] =>
  user.is_responsible_seed ? [null, user.id] : [user.id];

// Documents brought in by the pending pole/criteria requests of those owners.
// An approved member is read-only, but a self-management request opens a new
// review cycle: only what it brought is up for a verdict, and the back end
// ties those documents to the request (`change_request_id`), which is what
// `ChangeRequestData.documents` lists.
export const getPendingChangeRequestDocIds = (
  changeRequests: ChangeRequestData[],
  ownerIds: (number | null)[]
): Set<number> => {
  const docIds = new Set<number>();

  changeRequests.forEach((request) => {
    if (request.status !== ChangeRequestStatus.PENDING) return;
    if (!pendingEngineeringRequestTypes.includes(request.request_type)) return;
    if (!ownerIds.includes(request.notice_company_user_id)) return;

    request.documents.forEach((document) => docIds.add(document.id));
  });

  return docIds;
};

// `criteria-documents` stopped being a list and became `{ groups, others }`
// (REAL-3248). Without checking the shape, an API that has not shipped the
// change blows the table up on `groups.map`; validating here turns the error
// into a snackbar and the accordion shows the failure state, like any other
// invalid response.
export const isCriteriaAnalysisData = (
  data: unknown
): data is CriteriaAnalysisData => {
  const payload = data as CriteriaAnalysisData | undefined;
  return Array.isArray(payload?.groups) && Array.isArray(payload?.others);
};

export const hasAdministrativeDocuments = (
  approvalStatus: ApprovalStatus | undefined
): boolean =>
  approvalStatus === ApprovalStatus.AWAITING_FORMALIZATION ||
  approvalStatus === ApprovalStatus.FORMALIZATION_SUBMITTED ||
  approvalStatus === ApprovalStatus.APPROVED ||
  approvalStatus === ApprovalStatus.DISCREDITED;

export const getAdditionTerm = (
  user: NoticeCompanyTeamUser
): AdditionTermData | undefined =>
  user.addition_term && user.addition_term.state !== TermState.NOT_REQUIRED
    ? user.addition_term
    : undefined;

export const getRemovalTerm = (
  user: NoticeCompanyTeamUser
): RemovalTermData | undefined =>
  user.removal_term && user.removal_term.state !== TermState.NOT_REQUIRED
    ? user.removal_term
    : undefined;

export const getTeamMemberTermHint = (
  user: NoticeCompanyTeamUser
): string | undefined => {
  const term = getRemovalTerm(user) ?? getAdditionTerm(user);

  if (term?.state === TermState.AWAITING_REVIEW) {
    return Constants.teamTermUnderReviewTooltip;
  }

  if (term?.state === TermState.AWAITING_SIGNATURE) {
    return Constants.teamTermAwaitingSignatureTooltip;
  }

  return undefined;
};

export const isEngineeringFrozenByAdditionTerm = (
  user: NoticeCompanyTeamUser
): boolean => {
  const term = getAdditionTerm(user);
  return (
    Boolean(term?.documents_approved_at) && term?.state !== TermState.APPROVED
  );
};
